const RoomNameSimilarity = require('./RoomNameSimilarity');
const { dealSupplierExtractor } = require('./DealSupplier');
const { DEAL_STATUS } = require('../constants/DealConstants');

const CATERING_ROOM_ONLY = 'Room Only';

const BOARD_RANK = {
    'Room Only': 0,
    'Breakfast Included': 1,
    'Half Board': 2,
    'Full Board': 3,
    'All Inclusive': 4,
};

const DIRECTION = {
    WORSE: -1,
    SAME: 0,
    BETTER: 1,
};

const REJECTION_REASON = {
    ROOM_CHANGED: 'room_changed',
    BOARD_DOWNGRADE: 'board_downgrade',
    CANCELLATION_DOWNGRADE: 'cancellation_downgrade',
    BOARD_AND_CANCELLATION_DOWNGRADE: 'board_and_cancellation_downgrade',
};

const RECHECK_TYPE = {
    LAZY: 'lazy',
    UPFRONT: 'upfront',
};

const UNAVAILABLE_REASON = {
    NO_ALTERNATIVE: 'no_alternative',
    ALTERNATIVE_REJECTED: 'alternative_rejected',
    TERMS_DOWNGRADED: 'terms_downgraded',
    DEAL_NO_LONGER_RELEVANT: 'deal_no_longer_relevant',
};

const BLOCKED_REASON = {
    NO_CLICKED_ROOM_NAME: 'no_clicked_room_name',
    NO_CANDIDATES: 'no_candidates',
    NO_NAME_MATCH: 'no_name_match',
    NAME_CONFLICT: 'name_conflict',
    TERMS_WORSE: 'terms_worse',
};

const PRICE_TOLERANCE = 1;
const WHITESPACE_REGEX = /\s+/g;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const CANCELLATION_DOWNGRADE_TOLERANCE_IN_DAYS = 3;

function getBoardRank(board) {
    const rank = BOARD_RANK[board || CATERING_ROOM_ONLY];
    return typeof rank === 'number' ? rank : BOARD_RANK[CATERING_ROOM_ONLY];
}

function getBoard(deal) {
    return (deal && deal.catering) || CATERING_ROOM_ONLY;
}

function getRoomName(deal) {
    const name = deal && (deal.description || deal.room);
    return typeof name === 'string' && name.trim() ? name : null;
}

function normalizeRoomName(name) {
    return name.trim().toLowerCase().replace(WHITESPACE_REGEX, ' ');
}

function compareRoom(original_deal, alternative_deal) {
    if (!original_deal || !alternative_deal || original_deal.id === alternative_deal.id) {
        return DIRECTION.SAME;
    }

    const old_name = getRoomName(original_deal);
    const new_name = getRoomName(alternative_deal);
    if (!old_name || !new_name) {
        return DIRECTION.SAME;
    }

    return normalizeRoomName(old_name) === normalizeRoomName(new_name) ? DIRECTION.SAME : DIRECTION.WORSE;
}

function getCancellationDeadlineDayKey(deadline) {
    if (!deadline) {
        return null;
    }
    const date = new Date(deadline);
    if (Number.isNaN(date.getTime())) {
        return null;
    }
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

// A deal only carries chooseprod once it has been rechecked; the deal the user clicked on the
// hotel page has not been, so its policy has to be read from the raw search fields instead.
function getCancellationTerms(deal) {
    const dca = deal?.chooseprod?.dca;
    if (dca) {
        return { non_refundable: !!dca.nonRefundable, deadline: dca.dealIsFullyRefundableTill ?? null };
    }

    const details = deal?.original?.details;
    return {
        non_refundable: !!details?.non_refundable,
        deadline: details?.refundable_until ?? null,
    };
}

function getEffectiveCancellationDeadline(deal, now) {
    const { non_refundable, deadline } = getCancellationTerms(deal);

    if (non_refundable || !deadline) {
        return null;
    }
    const deadline_time = new Date(deadline).getTime();
    if (Number.isNaN(deadline_time)) {
        return null;
    }
    const reference = typeof now === 'number' ? now : Date.now();
    return deadline_time < reference ? null : deadline;
}

function compareBoard(old_board, new_board) {
    const delta = getBoardRank(new_board) - getBoardRank(old_board);
    if (delta === 0) {
        return DIRECTION.SAME;
    }
    return delta > 0 ? DIRECTION.BETTER : DIRECTION.WORSE;
}

function compareCancellation(old_deadline, new_deadline) {
    const old_key = getCancellationDeadlineDayKey(old_deadline);
    const new_key = getCancellationDeadlineDayKey(new_deadline);

    if (old_key === null && new_key === null) {
        return DIRECTION.SAME;
    }
    if (old_key === null) {
        return DIRECTION.BETTER;
    }
    if (new_key === null) {
        return DIRECTION.WORSE;
    }
    if (old_key === new_key) {
        return DIRECTION.SAME;
    }
    return new_key > old_key ? DIRECTION.BETTER : DIRECTION.WORSE;
}

function getCancellationDowngradeInDays(old_deadline, new_deadline) {
    const old_key = getCancellationDeadlineDayKey(old_deadline);
    const new_key = getCancellationDeadlineDayKey(new_deadline);

    if (old_key === null || new_key === null) {
        return null;
    }
    return Math.max(0, (old_key - new_key) / MS_PER_DAY);
}

function comparePrice(old_price, new_price) {
    if (!Number.isFinite(old_price) || !Number.isFinite(new_price)) {
        return DIRECTION.SAME;
    }
    const delta = new_price - old_price;
    if (Math.abs(delta) <= PRICE_TOLERANCE) {
        return DIRECTION.SAME;
    }
    return delta < 0 ? DIRECTION.BETTER : DIRECTION.WORSE;
}

function getRejectionReason(board_direction, cancellation_direction, cancellation_downgrade_days, room_direction) {
    if (room_direction === DIRECTION.WORSE) {
        return REJECTION_REASON.ROOM_CHANGED;
    }

    const board_worse = board_direction === DIRECTION.WORSE;
    const within_tolerance =
        cancellation_downgrade_days !== null && cancellation_downgrade_days <= CANCELLATION_DOWNGRADE_TOLERANCE_IN_DAYS;
    const cancellation_worse = cancellation_direction === DIRECTION.WORSE && !within_tolerance;

    if (board_worse && cancellation_worse) {
        return REJECTION_REASON.BOARD_AND_CANCELLATION_DOWNGRADE;
    }
    if (board_worse) {
        return REJECTION_REASON.BOARD_DOWNGRADE;
    }
    if (cancellation_worse) {
        return REJECTION_REASON.CANCELLATION_DOWNGRADE;
    }
    return null;
}

function getTermsRejectionReason(original_deal, candidate_deal, now) {
    const board_direction = compareBoard(getBoard(original_deal), getBoard(candidate_deal));
    const old_deadline = getEffectiveCancellationDeadline(original_deal, now);
    const new_deadline = getEffectiveCancellationDeadline(candidate_deal, now);

    return getRejectionReason(
        board_direction,
        compareCancellation(old_deadline, new_deadline),
        getCancellationDowngradeInDays(old_deadline, new_deadline),
        DIRECTION.SAME,
    );
}

function buildRecheckComparison(original_deal, alternative_deal, now) {
    const reference = typeof now === 'number' ? now : Date.now();

    const old_price = original_deal ? original_deal.totalPrice : null;
    const new_price = alternative_deal ? alternative_deal.totalPrice : null;
    const price_direction = comparePrice(old_price, new_price);

    const old_board = getBoard(original_deal);
    const new_board = getBoard(alternative_deal);
    const board_direction = compareBoard(old_board, new_board);

    const old_cancellation_deadline = getEffectiveCancellationDeadline(original_deal, reference);
    const new_cancellation_deadline = getEffectiveCancellationDeadline(alternative_deal, reference);
    const cancellation_direction = compareCancellation(old_cancellation_deadline, new_cancellation_deadline);
    const cancellation_downgrade_days = getCancellationDowngradeInDays(
        old_cancellation_deadline,
        new_cancellation_deadline,
    );

    const old_room = getRoomName(original_deal);
    const new_room = getRoomName(alternative_deal);
    const room_direction = compareRoom(original_deal, alternative_deal);

    const rejection_reason = getRejectionReason(
        board_direction,
        cancellation_direction,
        cancellation_downgrade_days,
        room_direction,
    );

    return {
        old_price,
        new_price,
        price_changed: price_direction !== DIRECTION.SAME,
        price_direction,
        old_room,
        new_room,
        room_changed: room_direction !== DIRECTION.SAME,
        old_board,
        new_board,
        board_changed: board_direction !== DIRECTION.SAME,
        board_direction,
        old_cancellation_deadline,
        new_cancellation_deadline,
        deadline_changed: cancellation_direction !== DIRECTION.SAME,
        cancellation_direction,
        cancellation_downgrade_days,
        acceptable: !rejection_reason,
        rejection_reason,
    };
}

function hasRoomNameChanged(old_room, new_room) {
    return !!old_room && !!new_room && normalizeRoomName(old_room) !== normalizeRoomName(new_room);
}

function hasAnyDealChange(comparison) {
    if (!comparison) {
        return false;
    }
    return (
        !!comparison.price_changed ||
        !!comparison.board_changed ||
        comparison.old_board !== comparison.new_board ||
        !!comparison.deadline_changed ||
        !!comparison.room_changed ||
        hasRoomNameChanged(comparison.old_room, comparison.new_room)
    );
}

function getLazyRecheckFlags(company_settings) {
    const changes_detected = !!company_settings?.show_changes_detected_on_lazy_recheck;
    return {
        changes_detected,
        suggest_alternative: changes_detected && !!company_settings?.suggest_alternative_on_lazy_recheck,
    };
}

function getCheapestDeal(deals) {
    return (deals || [])
        .filter((deal) => Number.isFinite(deal?.totalPrice))
        .reduce((cheapest, deal) => (!cheapest || deal.totalPrice < cheapest.totalPrice ? deal : cheapest), null);
}

function pickBestAcceptableAlternative(original_deal, alternative_deals, now) {
    const reference = typeof now === 'number' ? now : Date.now();

    const acceptable = (alternative_deals || [])
        .map((deal) => ({ deal, comparison: buildRecheckComparison(original_deal, deal, reference) }))
        .filter(({ comparison }) => comparison.acceptable);

    const cheapest = getCheapestDeal(acceptable.map(({ deal }) => deal));

    return acceptable.find(({ deal }) => deal === cheapest) || null;
}

const DEAL_STATUS_UNAVAILABLE = [DEAL_STATUS.NA, DEAL_STATUS.FAIL];

function isBookableCandidate(deal) {
    return (
        !!deal &&
        !deal.hide &&
        !deal.faultDeal &&
        !DEAL_STATUS_UNAVAILABLE.includes(deal.status) &&
        Number.isFinite(deal.totalPrice)
    );
}

function describeDeal(deal, now) {
    if (!deal) {
        return null;
    }
    return {
        id: deal.id,
        room: getRoomName(deal),
        price: Number.isFinite(deal.totalPrice) ? deal.totalPrice : null,
        board: getBoard(deal),
        cancellation_deadline: getEffectiveCancellationDeadline(deal, now),
        group_id: deal.superseded_group_id ?? null,
        supplier: dealSupplierExtractor(deal).supplier ?? null,
    };
}

function findBestSimilarInList(clicked_deal, candidate_deals, now) {
    const clicked_room = getRoomName(clicked_deal);
    if (!clicked_room) {
        return {
            match: null,
            similarity: null,
            near_miss: null,
            considered: 0,
            blocked_reason: BLOCKED_REASON.NO_CLICKED_ROOM_NAME,
        };
    }

    const cross_group = (candidate_deals || []).filter(
        (deal) => !deal.superseded && deal.superseded_group_id !== clicked_deal.superseded_group_id,
    );
    if (!cross_group.length) {
        return {
            match: null,
            similarity: null,
            near_miss: null,
            considered: 0,
            blocked_reason: BLOCKED_REASON.NO_CANDIDATES,
        };
    }

    let any_name_matched = false;
    let best_near_miss = null;
    const acceptable_similar = [];

    cross_group.forEach((deal) => {
        const similarity = RoomNameSimilarity.compareRoomNames(clicked_room, getRoomName(deal));
        if (!similarity.matched) {
            // The closest name that did NOT match, so DICE_MATCH_THRESHOLD and the conflict groups
            // can be re-tuned from the data instead of from a redeploy.
            if (!best_near_miss || similarity.score > best_near_miss.score) {
                best_near_miss = { score: similarity.score, room: getRoomName(deal), conflict_group: similarity.conflict_group };
            }
            return;
        }
        any_name_matched = true;
        if (getTermsRejectionReason(clicked_deal, deal, now)) {
            return;
        }
        acceptable_similar.push({ deal, similarity });
    });

    if (!acceptable_similar.length) {
        return {
            match: null,
            similarity: null,
            near_miss: best_near_miss,
            considered: cross_group.length,
            blocked_reason: any_name_matched
                ? BLOCKED_REASON.TERMS_WORSE
                : best_near_miss?.conflict_group
                  ? BLOCKED_REASON.NAME_CONFLICT
                  : BLOCKED_REASON.NO_NAME_MATCH,
        };
    }

    const cheapest = acceptable_similar.reduce((best, entry) =>
        entry.deal.totalPrice < best.deal.totalPrice ? entry : best,
    );

    return {
        match: cheapest.deal,
        similarity: cheapest.similarity,
        near_miss: best_near_miss,
        considered: cross_group.length,
        blocked_reason: null,
    };
}

function buildAlternativesSnapshot({ clicked_deal, candidate_deals, now }) {
    const reference = typeof now === 'number' ? now : Date.now();

    if (!clicked_deal) {
        return null;
    }

    const pool = (candidate_deals || []).filter((deal) => deal.id !== clicked_deal.id && isBookableCandidate(deal));

    const same_group = pool.filter((deal) => deal.superseded_group_id === clicked_deal.superseded_group_id);
    const best_alternative = pickBestAcceptableAlternative(clicked_deal, same_group, reference);
    const rejected_comparison = best_alternative ? null : buildRejectedComparison(clicked_deal, same_group, reference);

    const { match, similarity, near_miss, considered, blocked_reason } = findBestSimilarInList(
        clicked_deal,
        pool,
        reference,
    );

    const alternative_price = best_alternative?.deal?.totalPrice ?? null;
    const match_price = match?.totalPrice ?? null;
    const clicked_price = clicked_deal.totalPrice ?? null;

    return {
        clicked: describeDeal(clicked_deal, reference),
        would_be_alternative: describeDeal(best_alternative?.deal ?? null, reference),
        alternative_rejection_reason: rejected_comparison?.rejection_reason ?? null,
        best_similar_in_list: describeDeal(match, reference),
        match_similarity: similarity?.score ?? null,
        match_matched_by: similarity?.matched_by ?? null,
        match_blocked_reason: blocked_reason,
        near_miss_similarity: near_miss?.score ?? null,
        near_miss_room: near_miss?.room ?? null,
        // Two different populations: everything bookable on the page, and the cross-group,
        // non-superseded subset the name matcher actually looked at. A match rate computed
        // against the first would read artificially low.
        candidates_considered: pool.length,
        match_candidates_considered: considered,
        // Positive means we would have sent the user to a pricier room than a similar-looking one
        // already on the page. Null when there was no alternative to substitute, so "no regret
        // possible" is never read as "regret of zero".
        regret_delta:
            Number.isFinite(alternative_price) && Number.isFinite(match_price) ? alternative_price - match_price : null,
        // The same question asked of the deal the user actually clicked, which survives when the
        // clicked deal is alone in its mapping group and there is no alternative to compare.
        // Positive means a cheaper similar-looking room was visible while we showed this one.
        clicked_vs_match_delta:
            Number.isFinite(clicked_price) && Number.isFinite(match_price) ? clicked_price - match_price : null,
        prices_verified: false,
    };
}

// The terms of the deal the user clicked, reportable even when the recheck returned
// nothing to compare it against.
function buildOriginalTerms(original_deal, now) {
    return {
        old_price: original_deal?.totalPrice ?? null,
        old_board: original_deal ? getBoard(original_deal) : null,
        old_cancellation_deadline: getEffectiveCancellationDeadline(original_deal, now),
    };
}

function buildRejectedComparison(original_deal, alternative_deals, now) {
    const cheapest = getCheapestDeal(alternative_deals);
    return cheapest ? buildRecheckComparison(original_deal, cheapest, now) : null;
}

module.exports = {
    CATERING_ROOM_ONLY,
    BLOCKED_REASON,
    BOARD_RANK,
    DIRECTION,
    REJECTION_REASON,
    RECHECK_TYPE,
    UNAVAILABLE_REASON,
    getBoardRank,
    getBoard,
    getRoomName,
    compareRoom,
    CANCELLATION_DOWNGRADE_TOLERANCE_IN_DAYS,
    getCancellationDeadlineDayKey,
    getCancellationDowngradeInDays,
    getCancellationTerms,
    getEffectiveCancellationDeadline,
    getTermsRejectionReason,
    compareBoard,
    compareCancellation,
    comparePrice,
    buildRecheckComparison,
    buildOriginalTerms,
    getCheapestDeal,
    pickBestAcceptableAlternative,
    buildRejectedComparison,
    buildAlternativesSnapshot,
    hasAnyDealChange,
    getLazyRecheckFlags,
};
